import type { CheckoutOfferKey } from "../integrations/stripe-checkout";

export type OfferId = "7" | "3" | "5";

export interface OfferContent {
  id: OfferId;
  checkoutKey: CheckoutOfferKey;
  slug: string;
  name: string;
  headline: string;
  subhead: string;
  price: string;
  priceNote: string;
  promise: string;
  problems: string[];
  outcomes: string[];
  modules: { title: string; summary: string }[];
  compliance: string[];
  cta: string;
}

export const OFFERS: Record<string, OfferContent> = {
  "financial-freedom": {
    id: "7",
    checkoutKey: "offer7",
    slug: "financial-freedom",
    name: "Aviation Financial Freedom Blueprint",
    headline: "A pilot-specific wealth plan—without guesswork.",
    subhead:
      "Education on taxes, retirement tradeoffs, protection, and estate basics. Individual tax advice requires a licensed CPA or attorney.",
    price: "$4,995",
    priceNote: "Payment plan where offered: $1,249 × 4 (see checkout).",
    promise:
      "Build a defensible framework for how you think about income, retirement sources, and risk—aligned to aviation careers.",
    problems: [
      "Multi-state and per-diem complexity that generic advisors misunderstand.",
      "Pension vs 401(k) tradeoffs without a written decision frame.",
      "Insurance overlap or gaps relative to obligations.",
    ],
    outcomes: [
      "Clarity on questions worth taking to your CPA or planner.",
      "A structured module path through tax topics, retirement, debt, and protection.",
      "Worksheets you can maintain as your situation changes.",
    ],
    modules: [
      { title: "Tax mastery for pilots", summary: "Income characterization and record-keeping at education level." },
      { title: "Pension vs 401(k) strategy", summary: "Tradeoffs and questions for your qualified advisor." },
      { title: "Asset protection", summary: "Concepts, not legal advice." },
      { title: "Wealth building blueprint", summary: "Savings behavior and diversification framing." },
      { title: "Debt optimization", summary: "Paydown sequencing and good vs bad debt." },
      { title: "Business structuring", summary: "When side income triggers planning." },
      { title: "Insurance strategy", summary: "Life, disability, umbrella—aligned to obligations." },
      { title: "Estate planning basics", summary: "Beneficiaries, documents checklist." },
    ],
    compliance: [
      "No guaranteed tax savings or investment returns.",
      "Educational positioning only; not individualized tax or legal advice.",
    ],
    cta: "Join the waitlist — we will email when enrollment opens.",
  },
  "decision-mastery": {
    id: "3",
    checkoutKey: "offer3",
    slug: "decision-mastery",
    name: "Decision-Making Under Stress",
    headline: "Repeatable decision quality under time pressure.",
    subhead:
      "Six-week cohort: CRM-aligned frameworks, scenario practice, and reflection—without sensationalism.",
    price: "$2,495",
    priceNote: "6-week cohort · 2 live sessions per week (format per schedule).",
    promise:
      "Build habits to recognize bias, apply a structured framework, and communicate clearly when ambiguity is high.",
    problems: [
      "Stress amplifies cognitive bias and weak communication.",
      "Checklist knowledge without a decision tree for novel situations.",
      "Limited safe spaces to debrief without career risk.",
    ],
    outcomes: [
      "Name common biases in realistic scenarios.",
      "Apply a structured decision process (e.g., FORDEC-style).",
      "Improve CRM-aligned communication patterns.",
    ],
    modules: [
      { title: "Stress physiology", summary: "Arousal curve and recovery basics." },
      { title: "FORDEC framework", summary: "Steps, triggers, escalation." },
      { title: "Cognitive biases", summary: "Seven patterns with educational aviation examples." },
      { title: "CRM applied", summary: "Briefings, challenges, SOP alignment." },
      { title: "Real NTSB cases", summary: "Lessons cited responsibly—no sensationalism." },
      { title: "Personal decision audit", summary: "Habits, preparation, peer feedback." },
    ],
    compliance: [
      "No medical claims; professional help for clinical issues.",
      "Educational content must not encourage shortcuts on SOPs.",
    ],
    cta: "Reserve a cohort seat — limited availability per intake.",
  },
  "career-pivot": {
    id: "5",
    checkoutKey: "offer5",
    slug: "career-pivot",
    name: "Pilot Career Pivot Consulting",
    headline: "Translate cockpit experience into hiring systems.",
    subhead:
      "Twelve weeks with weekly 1:1 coaching: positioning, materials, interviews, negotiation, first 90 days.",
    price: "$5,995",
    priceNote: "12-week program · weekly 60-minute sessions (as scheduled).",
    promise:
      "Structured transition with accountability—resume, LinkedIn, pipeline, and reps—without starting from zero.",
    problems: [
      "Underestimating how to translate ATP experience for non-aviation screens.",
      "Weak network effects outside aviation bubbles.",
      "Negotiation and comp framing without a plan.",
    ],
    outcomes: [
      "Clarity on target roles aligned to strengths.",
      "Materials that pass ATS and human review.",
      "Interview reps and a negotiation plan you can execute.",
    ],
    modules: [
      { title: "Career assessment", summary: "Skills, constraints, family factors." },
      { title: "Resume optimization", summary: "Translation table for achievements." },
      { title: "LinkedIn strategy", summary: "Headline, featured, network growth." },
      { title: "Interview mastery", summary: "Behavioral stories and question bank." },
      { title: "Salary negotiation", summary: "Anchors, timing, written offers." },
      { title: "Job search systems", summary: "Pipeline metrics and CRM for leads." },
      { title: "Alternative careers", summary: "Paths by risk profile." },
      { title: "90-day action plan", summary: "Milestones after offer." },
    ],
    compliance: [
      "No false placement guarantees.",
      "Testimonials must be substantiated when published.",
    ],
    cta: "Apply for a fit call — we review capacity before onboarding.",
  },
};

export const OFFER_ORDER = ["financial-freedom", "decision-mastery", "career-pivot"] as const;
